import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, ArrowLeft, Check, Loader2, AlertCircle, Info } from 'lucide-react';
import { bookingsAPI, paymentsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const PaymentPage = () => {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [paymentOption, setPaymentOption] = useState('deposit'); // 'deposit' or 'full'

  const sessionId = searchParams.get('session_id');

  const fetchBooking = useCallback(async () => {
    try {
      const response = await bookingsAPI.getOne(bookingId);
      setBooking(response.data);
      // If deposit already paid, default to full payment
      if (response.data.deposit_paid) {
        setPaymentOption('full');
      }
    } catch (error) {
      toast.error('Failed to load booking details');
      navigate('/customer/dashboard');
    } finally {
      setLoading(false);
    }
  }, [bookingId, navigate]);

  const pollPaymentStatus = useCallback(async (sessionId) => {
    setCheckingStatus(true);
    const maxAttempts = 10;
    let attempts = 0;
    
    const checkStatus = async () => {
      try {
        const response = await paymentsAPI.checkStatus(bookingId);
        if (response.data.payment_status === 'paid' || response.data.payment_status === 'deposit_paid') {
          toast.success(response.data.payment_status === 'paid' 
            ? 'Payment successful! Thank you for your booking.' 
            : 'Deposit received! Your booking is confirmed.');
          navigate(`/customer/dashboard`);
          return true;
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      }
      return false;
    };

    while (attempts < maxAttempts) {
      const paid = await checkStatus();
      if (paid) return;
      await new Promise(resolve => setTimeout(resolve, 2000));
      attempts++;
    }
    
    toast.info('Payment is being processed. Please check your booking status.');
    navigate(`/customer/dashboard`);
  }, [bookingId, navigate]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      fetchBooking();
    }
  }, [sessionId, pollPaymentStatus, fetchBooking]);

  const handlePayment = async () => {
    setProcessing(true);
    try {
      const response = await paymentsAPI.createCheckout({
        booking_id: bookingId,
        payment_method: 'stripe',
        payment_type: paymentOption // 'deposit' or 'full'
      });
      
      if (response.data.checkout_url) {
        try {
          if (window.top !== window.self) {
            window.top.location.href = response.data.checkout_url;
          } else {
            window.location.href = response.data.checkout_url;
          }
        } catch (e) {
          window.open(response.data.checkout_url, '_blank');
        }
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process payment');
      setProcessing(false);
    }
  };

  // Calculate amounts
  const totalAmount = booking?.total_amount || 0;
  const depositAmount = Math.ceil(totalAmount * 0.2 * 100) / 100; // 20% deposit, rounded up
  const remainingAmount = totalAmount - (booking?.deposit_amount || depositAmount);
  const amountToPay = paymentOption === 'deposit' ? depositAmount : 
                      booking?.deposit_paid ? remainingAmount : totalAmount;

  if (loading || checkingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 to-teal-50">
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <Loader2 className="w-12 h-12 text-[var(--primary)] mx-auto animate-spin" />
          <p className="mt-4 text-[var(--text-secondary)]">
            {checkingStatus ? 'Verifying payment...' : 'Loading booking details...'}
          </p>
        </motion.div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 to-teal-50">
        <div className="card p-8 text-center">
          <AlertCircle className="w-12 h-12 text-[var(--error)] mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Booking Not Found</h2>
          <p className="text-[var(--text-muted)] mb-4">Unable to load booking details</p>
          <Link to="/customer/dashboard">
            <Button>Return to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (booking.payment_status === 'paid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 to-teal-50">
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="card p-8 text-center max-w-md"
        >
          <div className="w-16 h-16 bg-[var(--success)]/15 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-[var(--success)]" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Payment Complete!</h2>
          <p className="text-[var(--text-muted)] mb-6">Your booking has been confirmed and fully paid.</p>
          <Link to="/customer/dashboard">
            <Button className="w-full">View My Bookings</Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-teal-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Link to="/customer/dashboard" className="inline-flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--primary)] mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="card overflow-hidden"
        >
          {/* Header */}
          <div className="p-6 bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] text-white">
            <h1 className="text-2xl font-bold font-['Cormorant_Garamond']">Complete Payment</h1>
            <p className="opacity-90 mt-1">Secure checkout powered by Stripe</p>
          </div>

          {/* Booking Summary */}
          <div className="p-6 border-b">
            <h2 className="font-semibold mb-4">Booking Summary</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">Service</span>
                <span className="font-medium">{booking.service_name || booking.service_type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">Date</span>
                <span className="font-medium">{booking.scheduled_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">Time</span>
                <span className="font-medium">{booking.scheduled_time}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">Duration</span>
                <span className="font-medium">{booking.duration_hours} hours</span>
              </div>
              {booking.mileage_fee > 0 && (
                <div className="flex justify-between text-amber-600">
                  <span>Mileage Fee</span>
                  <span className="font-medium">${booking.mileage_fee.toFixed(2)}</span>
                </div>
              )}
              <div className="pt-3 border-t flex justify-between text-lg">
                <span className="font-semibold">Total</span>
                <span className="font-bold text-[var(--primary)]">${totalAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Payment Options */}
          <div className="p-6 border-b">
            <h2 className="font-semibold mb-4">Payment Option</h2>
            
            {booking.deposit_paid ? (
              // Deposit already paid - show remaining balance
              <div className="bg-[var(--success)]/10 border border-[var(--success)]/30 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 text-green-700 mb-2">
                  <Check className="w-5 h-5" />
                  <span className="font-semibold">Deposit Paid</span>
                </div>
                <p className="text-sm text-[var(--success)]">
                  Your 20% deposit of ${(booking.deposit_amount || depositAmount).toFixed(2)} has been received.
                </p>
              </div>
            ) : (
              // Show deposit or full payment options
              <div className="space-y-3">
                {/* Deposit Option */}
                <label 
                  className={`block border-2 rounded-xl p-4 cursor-pointer transition-all ${
                    paymentOption === 'deposit' 
                      ? 'border-[var(--primary)] bg-rose-50/50' 
                      : 'border-[var(--border-primary)] hover:border-[var(--border-primary)]'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="payment_option"
                      value="deposit"
                      checked={paymentOption === 'deposit'}
                      onChange={() => setPaymentOption('deposit')}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="font-semibold">Pay 20% Deposit Now</span>
                          <span className="ml-2 px-2 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37] text-xs rounded-full">Recommended</span>
                        </div>
                        <span className="font-bold text-[var(--primary)]">${depositAmount.toFixed(2)}</span>
                      </div>
                      <p className="text-sm text-[var(--text-muted)] mt-1">
                        Secure your booking now, pay the remaining ${remainingAmount.toFixed(2)} before service.
                      </p>
                    </div>
                  </div>
                </label>

                {/* Full Payment Option */}
                <label 
                  className={`block border-2 rounded-xl p-4 cursor-pointer transition-all ${
                    paymentOption === 'full' 
                      ? 'border-[var(--primary)] bg-rose-50/50' 
                      : 'border-[var(--border-primary)] hover:border-[var(--border-primary)]'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="payment_option"
                      value="full"
                      checked={paymentOption === 'full'}
                      onChange={() => setPaymentOption('full')}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <span className="font-semibold">Pay Full Amount</span>
                        <span className="font-bold text-[var(--primary)]">${totalAmount.toFixed(2)}</span>
                      </div>
                      <p className="text-sm text-[var(--text-muted)] mt-1">
                        Complete your payment in one transaction.
                      </p>
                    </div>
                  </div>
                </label>
              </div>
            )}

            {/* Info Box */}
            <div className="mt-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-3 flex gap-2">
              <Info className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <div className="text-sm text-[#D4AF37]">
                {booking.deposit_paid ? (
                  <p>Pay the remaining balance before your scheduled service date to confirm your appointment.</p>
                ) : (
                  <p>A minimum 20% deposit is required to confirm your booking. The remaining balance is due before your service date.</p>
                )}
              </div>
            </div>
          </div>

          {/* Payment Method */}
          <div className="p-6">
            <h2 className="font-semibold mb-4">Payment Method</h2>
            
            <div className="border-2 border-[var(--primary)] rounded-xl p-4 bg-rose-50/50">
              <div className="flex items-center gap-3">
                <CreditCard className="w-6 h-6 text-[var(--primary)]" />
                <div>
                  <span className="font-semibold">Credit/Debit Card</span>
                  <span className="text-xs text-[var(--text-muted)] block">Secure payment via Stripe</span>
                </div>
              </div>
            </div>

            <p className="text-sm text-[var(--text-muted)] mt-4 text-center">
              You'll be redirected to Stripe to complete your payment securely.
            </p>

            <Button 
              className="w-full mt-6"
              size="lg"
              onClick={handlePayment}
              disabled={processing}
              data-testid="pay-now-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Redirecting to Stripe...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  Pay ${amountToPay.toFixed(2)} with Stripe
                </>
              )}
            </Button>

            {/* Amount Breakdown */}
            {!booking.deposit_paid && paymentOption === 'deposit' && (
              <div className="mt-4 text-center text-sm text-[var(--text-muted)]">
                <p>Paying now: <strong>${depositAmount.toFixed(2)}</strong> (20% deposit)</p>
                <p>Due later: <strong>${remainingAmount.toFixed(2)}</strong></p>
              </div>
            )}
          </div>

          {/* Security Badge */}
          <div className="px-6 pb-6">
            <div className="flex items-center justify-center gap-2 text-sm text-[var(--text-muted)]">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              Secured by Stripe | 256-bit SSL encryption
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentPage;
